import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useCompanyFeatureFlags } from '@/app-config/featureFlags'
import {
	APIHookCreatorConfig,
	APIHookOptions,
	createAPIHook,
	createApiHookWithLoadingState,
	createLoadOnceAPIHook,
	createPollAPIHook,
	useUser,
} from '@/lib/react-hooks/generic-hooks'
import { getFailedBookingInfo } from '@/lib/trip-utils'
import { isApprover } from '@/lib/user-utils'
import {
	EssentialTravelInformationActions,
	EssentialTravelInformationTypes,
	ItineraryActions,
	ItineraryTypes,
	RecentSearchesActions,
	RecentSearchesTypes,
	TripActions,
	TripPurposeTypes,
	TripTypes,
} from '@/redux/actions'
import { ApplicationState } from '@/redux/stores/types'
import {
	EssentialTravelInformation,
	Itinerary,
	SearchData,
	Trip,
	WithBookingSegmentToTravelers,
	WithReservationStatus,
} from '@/travelsuit'
import { TripFeeStatusData } from '@/types/trip'
import { User } from '@/types/user'

export const useTripOrders = createLoadOnceAPIHook<{ id: number }, TripFeeStatusData[] | undefined>({
	loadAction: ({ id }, { onLoad, onError }) => TripActions.getBookingStatus(id, onLoad, onError),
	loadingSelectors: TripTypes.GetBookingStatus.ORIGINAL,
	storeMapper: ({ tripOrders }, { id }) => tripOrders.get(id),
	isEmpty: (orders) => !orders,
})

export const useTrip = createAPIHook<{ id: number }, Trip | undefined>({
	loadAction: ({ id }, { onLoad, onError }) => TripActions.getTrip(id, onLoad, onError),
	loadingSelectors: TripTypes.GetTrip.ORIGINAL,
	storeMapper: ({ trips }, { id }) => trips.byId.get(id),
	mapParamsToHookDeps: (params) => [params.id],
})

const LOAD_ITINERARY_CONFIG: APIHookCreatorConfig<{ tripId?: number }, Itinerary | undefined> = {
	loadAction: ({ tripId }, { onLoad, onError }) =>
		tripId ? ItineraryActions.getItinerary(tripId, onLoad, onError) : { type: 'Empty' },
	loadingSelectors: ItineraryTypes.GetItinerary.ORIGINAL,
	storeMapper: ({ itinerary }, { tripId }) => (tripId ? itinerary.get(tripId)?.itinerary : undefined),
}

export const useItinerary = createAPIHook(LOAD_ITINERARY_CONFIG)

export const usePollItinerary = createPollAPIHook({ ...LOAD_ITINERARY_CONFIG, pollInterval: 2000 })

export function useTripApprovers(options: Partial<APIHookOptions> & { tripId: number }): User[] {
	const approvers = useItinerary(options)?.approvers
	return useMemo(() => approvers ?? [], [approvers])
}

export const useRecentSearches = createAPIHook<{ length?: number }, SearchData[]>({
	storeMapper: ({ recentSearches }) => recentSearches,
	loadAction: ({ length }, { onLoad, onError }) => RecentSearchesActions.getRecentSearches(length, onLoad, onError),
	loadingSelectors: RecentSearchesTypes.GetRecentSearches.ORIGINAL,
})

export const useTripPurposes = createApiHookWithLoadingState<{}, ApplicationState['tripPurposes']>({
	loadAction: (_, { onLoad, onError }) => ({ type: TripPurposeTypes.GetTripPurposes.REQUEST, onLoad, onError }),
	loadingSelectors: TripPurposeTypes.GetTripPurposes,
	storeMapper: ({ tripPurposes }) => tripPurposes,
})

export const useEssentialTravelInformation = createApiHookWithLoadingState<
	{ tripId: number },
	EssentialTravelInformation[]
>({
	loadAction: ({ tripId }, { onLoad, onError }) =>
		EssentialTravelInformationActions.getEssentialTravelInformation(tripId, onLoad, onError),
	loadingSelectors: EssentialTravelInformationTypes.GetEssentialTravelInformation,
	storeMapper: ({ essentialTravelInformation }) => essentialTravelInformation,
})

export function isUserApproverForTrip({ trip, itinerary, user }: { trip: Trip; itinerary?: Itinerary; user: User }) {
	const tripApprovals = trip.approval_statuses

	const approvers = itinerary?.approvers ?? []
	return isApprover(user, approvers, tripApprovals)
}

export function useIsCurrentUserApproverForTrip({ trip, itinerary }: { trip: Trip; itinerary?: Itinerary }) {
	const currentUser = useUser()
	return isUserApproverForTrip({ trip, itinerary, user: currentUser })
}

export function useFailedBookingInfo(
	booking?: (Partial<WithReservationStatus> & WithBookingSegmentToTravelers) | null,
) {
	const { t } = useTranslation()
	const isAlternativeFaresImprovementsEnabled = useCompanyFeatureFlags()?.enable_alternative_fares_improvements
	const { isBookingFailed, failedTitle } = getFailedBookingInfo({
		booking,
		isAlternativeFaresImprovementsEnabled,
		t,
	})

	return { isAlternativeFaresImprovementsEnabled, isBookingFailed, failedTitle }
}
